import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { type JSX, useState } from 'react';
import { Button, Checkbox, Dialog, Input, Textarea } from '~/components/primitives';
import { ApiRequestError } from '~/lib/api/client';
import { fetchProject } from '~/lib/api/projects';
import { type SaveResult, saveProjectAsTemplate } from '~/lib/tasks/api/blueprints';
import { FooterReason } from './FooterReason';
import { TemplateOpSummary } from './TemplateOpSummary';
import styles from './dialogs.module.css';

/**
 * F2.4 — «Сохранить как шаблон» из проекта. Половина инварианта решения:
 * шаблон создаётся одной кнопкой из живого проекта. Конфликт имени → выбор
 * «новая версия / переименовать» (никакого авто-версионирования на FE).
 */
export type SaveAsTemplateDialogProps = {
  project: { id: string; name: string };
  onClose: () => void;
};

export function SaveAsTemplateDialog({ project, onClose }: SaveAsTemplateDialogProps): JSX.Element {
  const qc = useQueryClient();
  // F61 (TPL-F4): имя шаблона по умолчанию не совпадает с именем проекта — два
  // объекта с одним именем путались в списках.
  const [name, setName] = useState(() => m.tmpl_default_template_name({ name: project.name }));
  const [description, setDescription] = useState('');
  // F61 круг 1 (UX, MAJOR): в шаблон по умолчанию не уносим людей — по §11.11 в шаблоне
  // роли, а не конкретные ответственные; включить можно явно.
  const [includeAssignees, setIncludeAssignees] = useState(false);
  const [excludeDone, setExcludeDone] = useState(true);
  const [conflictId, setConflictId] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorParams, setErrorParams] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<SaveResult | null>(null);

  // project-visibility (F12) — источник private: шаблон = entity-wide артефакт,
  // предупреждаем, что структура станет доступна всей компании.
  const detail = useQuery({
    queryKey: ['project', project.id],
    queryFn: () => fetchProject(project.id),
  });
  const sourceIsPrivate = detail.data?.visibility === 'private';

  const save = useMutation({
    mutationFn: (blueprintId?: string) =>
      saveProjectAsTemplate(project.id, {
        name: name.trim(),
        description: description || undefined,
        include_assignees: includeAssignees,
        exclude_done: excludeDone,
        ...(blueprintId ? { blueprint_id: blueprintId } : {}),
      }),
    onSuccess: (res) => {
      setResult(res);
      setConflictId(null);
      setErrorCode(null);
      qc.invalidateQueries({ queryKey: ['task-module', 'blueprints'] });
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.code === 'task_module.blueprint_name_taken') {
        const existing = (e.details as { existing_blueprint_id?: string } | undefined)
          ?.existing_blueprint_id;
        setConflictId(existing ?? null);
        setErrorCode(null);
      } else if (e instanceof ApiRequestError) {
        setErrorCode(e.code);
        setErrorParams((e.details ?? {}) as Record<string, unknown>);
      } else {
        setErrorCode('server.internal');
      }
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      /* W-3 — в состоянии «уже сохранено» заголовок продолжал звать сохранять. */
      title={result ? m.tmpl_saved_title() : m.tmpl_save_title()}
      size="md"
      /*
       * F100 (С-I, NC-61; § 10.6 п. 4): подвал — слот `footer` `Dialog` на КАЖДОМ шаге, а не
       * липкий блок внутри прокручиваемого тела: полоса прокрутки тела заходила в подвал,
       * разделитель обрывался на её ширине.
       */
      footer={
        result ? (
          <Button onClick={onClose}>{m.tmpl_open_templates()}</Button>
        ) : conflictId ? undefined : (
          <>
            {/* F61 круг 1 (UX): последствие закрытого проекта — у кнопки, как у
              «Дублировать», и называет людей, если они уходят в шаблон. */}
            {sourceIsPrivate && (
              <p className={styles.consequence} data-testid="template-private-consequence">
                <TriangleAlert size={14} aria-hidden="true" />
                <span>
                  {includeAssignees
                    ? m.tmpl_template_private_consequence_people()
                    : m.tmpl_template_private_consequence()}
                </span>
              </p>
            )}
            {/* F100 круг 4 (UI, MAJOR): причина выключенного «Сохранить» — слева в подвале. */}
            {!name.trim() && (
              <FooterReason id="save-template-reason" htmlFor="save-template-name">
                {m.tmpl_reason_name_required()}
              </FooterReason>
            )}
            <Button variant="ghost" onClick={onClose}>
              {m.tm_cancel()}
            </Button>
            <Button
              loading={save.isPending}
              disabled={!name.trim()}
              focusableWhenDisabled
              onDisabledClick={() => document.getElementById('save-template-name')?.focus()}
              onClick={() => save.mutate(undefined)}
              aria-describedby={!name.trim() ? 'save-template-reason' : undefined}
            >
              {/* F61 (TPL-F6): подпись действия не повторяет заголовок диалога. */}
              {m.tmpl_action_save_short()}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className={styles.body}>
          <div className={styles.successBox} role="status">
            <span className={styles.successTitle}>
              {/* F-D13, тот же класс, что в диалоге дубликата: заголовок уже
                  говорит «Шаблон сохранён», карточка повторяла его слово в
                  слово. Здесь остаётся то, чего в заголовке нет, — версия. */}
              {m.tmpl_saved_ok({ version: result.version })}
            </span>
          </div>
          <TemplateOpSummary skipped={result.skipped} warnings={result.warnings} />
        </div>
      ) : (
        <div className={styles.body}>
          <Input
            id="save-template-name"
            required
            label={m.tmpl_field_template_name()}
            data-autofocus="true"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setConflictId(null);
            }}
          />
          {/* F61 (TPL-F6): нативный растягиватель тянул диалог вширь — только по
              вертикали и до предела. */}
          <div className={styles.descArea}>
            <Textarea
              label={m.tmpl_field_description()}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          {/* F61 (TPL-F4): тот же словарь, что у «Дублировать» («Скопировать
              ответственных»), и без двойного отрицания: «Без завершённых» (галочка =
              исключить) стало «Включить завершённые задачи» (галочка = включить). */}
          <div className={styles.group}>
            <span className={styles.fieldLabel} id="tpl-copy-label">
              {m.tmpl_save_copy_group_label()}
            </span>
            <div className={styles.checks} role="group" aria-labelledby="tpl-copy-label">
              <Checkbox
                label={m.tmpl_opt_assignees()}
                checked={includeAssignees}
                onChange={(e) => setIncludeAssignees(e.target.checked)}
              />
              <Checkbox
                label={m.tmpl_opt_done_tasks()}
                checked={!excludeDone}
                onChange={(e) => setExcludeDone(!e.target.checked)}
              />
            </div>
          </div>

          {conflictId && (
            <div className={styles.conflict}>
              <span className={styles.conflictTitle}>{m.tmpl_conflict_title()}</span>
              {/* F100: кнопки выбора при конфликте — внутри плашки конфликта, не липкий подвал. */}
              <div className={styles.conflictActions}>
                <Button variant="ghost" onClick={() => setConflictId(null)}>
                  {m.tmpl_conflict_rename()}
                </Button>
                <Button loading={save.isPending} onClick={() => save.mutate(conflictId)}>
                  {m.tmpl_conflict_update()}
                </Button>
              </div>
            </div>
          )}

          {errorCode && (
            <div className={styles.error}>{m.error_by_code(errorCode, errorParams)}</div>
          )}
        </div>
      )}
    </Dialog>
  );
}
